import {
  Playbook,
  PlaybookChatTypes,
  PlaybookEditorTypes,
  PlaybookEmailTypes,
  PlaybookFastlaneTypes,
  PlaybookLandingPageTypes,
  PlaybookMarketplaceWebTypes,
  PlaybookMigratedTypes,
  PlaybookReportType,
  PlaybookStatus,
} from '@driftt/api-types'
import { equals, path, pathOr } from 'ramda'
import isURL from 'validator/lib/isURL'

import {
  ALL_CHAT_PLAYBOOK_TYPES,
  CHAT_PLAYBOOKS_REPORT_TYPES,
  EMAIL_PLAYBOOK_TYPES,
  FAST_LANE_REPORT_TYPES,
  LANDING_PAGE_REPORT_TYPES,
  MARKETPLACE_WEB_REPORT_TYPES,
  MIGRATED_PLAYBOOK_TYPES,
  PLAYBOOK_EDITOR_TYPES,
  REPORT_TYPES_TO_TYPES,
  ReportTypeOptions,
} from 'constants/types'
import { CtaTypes, SupportedPlaybookTypes } from 'types/Playbook'

export const resolvePlaybookType = (
  reportType: PlaybookReportType
): SupportedPlaybookTypes =>
  REPORT_TYPES_TO_TYPES[reportType] || SupportedPlaybookTypes.NOT_SUPPORTED

export const getPlaybookCampaignId: (
  playbook?: Playbook
) => number | undefined = path<number>(['actions', '0', 'action', 'campaignId'])

export const isChatPlaybook = (reportType: PlaybookChatTypes) =>
  CHAT_PLAYBOOKS_REPORT_TYPES.includes(reportType)

export const isMeetingBookingReportType = (reportType: string) =>
  PlaybookReportType.MEETING_BOOKING_BOT === reportType

export const isNewEditorPlaybook = (reportType: ReportTypeOptions) =>
  PLAYBOOK_EDITOR_TYPES.includes(reportType as PlaybookEditorTypes) ||
  FAST_LANE_REPORT_TYPES.includes(reportType as PlaybookFastlaneTypes)

export const isMarketplaceWebPlaybook = (
  reportType: PlaybookMarketplaceWebTypes
) => MARKETPLACE_WEB_REPORT_TYPES.includes(reportType)

export const isDraftEmailPlaybook = (
  reportType: PlaybookReportType,
  status: PlaybookStatus
) =>
  status === PlaybookStatus.DISABLED &&
  (reportType === PlaybookReportType.SEND_A_ONETIME_EMAIL ||
    reportType === PlaybookReportType.SEND_A_ONETIME_OUTBOUND_EMAIL)

export const isLandingPagePlaybook = (reportType: PlaybookLandingPageTypes) =>
  LANDING_PAGE_REPORT_TYPES.includes(reportType)

export const isNonLandingPageBotPlaybook = (reportType: unknown) => {
  return (
    ALL_CHAT_PLAYBOOK_TYPES.includes(reportType as ReportTypeOptions) &&
    !LANDING_PAGE_REPORT_TYPES.includes(reportType as PlaybookLandingPageTypes)
  )
}

export const isOldEmailPlaybook = (reportType: ReportTypeOptions) =>
  [
    PlaybookEmailTypes.SEND_AN_AUTOMATED_EMAIL,
    PlaybookEmailTypes.SEND_A_ONETIME_EMAIL,
  ].includes(reportType as PlaybookEmailTypes)

export const isOneTimeEmailPlaybook = (reportType: PlaybookEmailTypes) =>
  reportType === PlaybookEmailTypes.SEND_A_ONETIME_EMAIL ||
  reportType === PlaybookEmailTypes.MARKETING_ONE_TIME_SEQUENCE ||
  reportType === PlaybookEmailTypes.NEW_BLOG_CONTENT ||
  reportType === PlaybookEmailTypes.NEWSLETTER ||
  reportType === PlaybookEmailTypes.PRODUCT_ANNOUNCEMENT

export const isEmailPlaybook = (reportType: PlaybookEmailTypes) =>
  EMAIL_PLAYBOOK_TYPES.includes(reportType)

export const isFastLanePlaybook = (playbook?: Pick<Playbook, 'reportType'>) =>
  equals(
    pathOr(null, ['reportType'], playbook),
    PlaybookReportType.FORM_SUBMISSION_FAST_LANE
  )

export const isMeetingBookingPlaybook = (
  playbook?: Pick<Playbook, 'reportType'>
) =>
  equals(
    pathOr(null, ['reportType'], playbook),
    PlaybookReportType.MEETING_BOOKING_BOT
  )

/**
 * @deprecated Playbooks.meta.autoInstallFlow is deprecated and will be removed in the future.
 *
 * Check fastlaneSettings.fastlaneSettingsType === 'QUICK_SETUP' instead
 */
export const verifyFastlaneQuickSetupMeta = (playbook: Playbook) => {
  return playbook.meta ? playbook.meta.autoInstallFlow : false
}

export const isValidPlaybookToCloneAsBlock = (reportType: unknown) =>
  isChatPlaybook(reportType as PlaybookChatTypes) &&
  PLAYBOOK_EDITOR_TYPES.includes(reportType as PlaybookEditorTypes)

// LEGACY PLAYBOOK TYPE METHODS

export const isMigratedPlaybook = (reportType: PlaybookMigratedTypes) =>
  MIGRATED_PLAYBOOK_TYPES.includes(reportType)

export const ctaTypeToLabel = Object.freeze({
  [CtaTypes.CHAT]: 'Start a conversation',
  [CtaTypes.MEETING]: 'Schedule a meeting',
  [CtaTypes.URL]: 'Link to a URL',
  [CtaTypes.NONE]: 'No CTA',
})

export const shouldRenderCtaButton = (ctaType: CtaTypes) =>
  ctaType === CtaTypes.CHAT ||
  ctaType === CtaTypes.MEETING ||
  ctaType === CtaTypes.URL

export const shouldRenderWelcomeMessageCtaButton = (ctaType: CtaTypes) =>
  ctaType === CtaTypes.MEETING || ctaType === CtaTypes.URL

export const isAbmPlaybook = (reportType: PlaybookReportType) =>
  equals(reportType, PlaybookReportType.ABM) ||
  equals(reportType, PlaybookReportType.DRIVE_TRAFFIC_TARGETED_MESSAGE) ||
  equals(reportType, PlaybookReportType.TALK_TO_YOUR_OUTBOUND_PROSPECTS) ||
  equals(reportType, PlaybookReportType.EMPTY_WELCOME_MESSAGE)

export const showCtaSettings = (reportType: PlaybookReportType) =>
  !equals(reportType, PlaybookReportType.TALK_TO_YOUR_OUTBOUND_PROSPECTS)

export const showMessageAuthorSelect = (reportType: PlaybookReportType) =>
  equals(reportType, PlaybookReportType.BUILD_A_SUBSCRIBER_LIST) ||
  equals(reportType, PlaybookReportType.EMPTY_SLIDER) ||
  equals(reportType, PlaybookReportType.EMPTY_WELCOME_MESSAGE) ||
  equals(reportType, PlaybookReportType.EMPTY_TAKEOVER) ||
  equals(reportType, PlaybookReportType.PROMOTION_FULL_SCREEN_TAKEOVER)

export const showTitlePreview = (reportType: PlaybookReportType) =>
  !equals(reportType, PlaybookReportType.EMPTY_WELCOME_MESSAGE)

export const isValidUrlWithProtocol = (url: string) =>
  isURL(url, { require_protocol: true }) // eslint-disable-line

export const hasLegacyPlaybookView = (reportType: ReportTypeOptions) =>
  isMigratedPlaybook(reportType as PlaybookMigratedTypes)
